'use client';

import dynamic from 'next/dynamic';
import { useMemo, useState } from 'react';
import { ExternalLink, Landmark } from 'lucide-react';
import type { GrantMatch, GrantReport } from '@/lib/grants-embed/lookups';
import {
  bondSummary,
  type BondSummary,
  formatUsdCompact,
  formatUsdWhole,
  grantAwardLabel,
  grantReason,
  grantStatusLabel,
  reportHeadline,
} from '@/lib/grants-embed/format';

// Leaflet touches `window` at import time.
const GrantPeerMap = dynamic(() => import('./grant-peer-map'), {
  ssr: false,
  loading: () => <div className="h-full w-full bg-[var(--g-line)]/40" />,
});

function Headline({ report }: { report: GrantReport }) {
  const headline = reportHeadline(report);
  const name = report.districtName;

  if (headline.kind === 'per-pupil') {
    const { anchorPerPupil, peerMedianPerPupil } = headline;
    const ratio = Math.max(0.04, anchorPerPupil / peerMedianPerPupil);
    return (
      <div className="space-y-5">
        <h2 className="g-display max-w-[22ch] text-balance text-[clamp(1.75rem,4.5vw,2.75rem)] font-extrabold leading-[1.05]">
          Districts like yours bring in {formatUsdWhole(peerMedianPerPupil)} per student each year in federal grants. {name} brings in {formatUsdWhole(anchorPerPupil)}.
        </h2>
        <div className="max-w-xl space-y-2 text-sm" aria-hidden>
          <div className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-[var(--g-slate)]">Similar districts</span>
            <span className="h-3 flex-1 rounded-sm bg-[var(--g-ink)]" />
          </div>
          <div className="flex items-center gap-3">
            <span className="w-28 shrink-0 text-[var(--g-slate)]">{name.length > 16 ? 'Your district' : name}</span>
            <span className="h-3 flex-1">
              <span className="block h-3 rounded-sm bg-[var(--g-accent)]" style={{ width: `${ratio * 100}%` }} />
            </span>
          </div>
        </div>
        <p className="text-xs text-[var(--g-slate)]">
          Federal awards per student, averaged over the last five years: direct awards plus money passed through the state (sub-awards of $30K or more, so a conservative total). Median of {report.benchmark.peersWithFunding} comparable districts. Source: USAspending.gov.
        </p>
      </div>
    );
  }

  if (headline.kind === 'peer-dollars') {
    return (
      <div className="space-y-3">
        <p className="g-display text-[clamp(3rem,10vw,5.5rem)] font-extrabold leading-none tabular-nums">
          {formatUsdCompact(headline.total)}
        </p>
        <h2 className="g-display max-w-[30ch] text-balance text-[clamp(1.25rem,3vw,1.75rem)] font-bold leading-tight">
          {/* One string: the JSX compiler dropped the space between {name} and "hasn&apos;t". */}
          {`went to districts like yours through grant programs ${name} hasn't used.`}
        </h2>
        <p className="text-sm text-[var(--g-slate)]">
          {headline.peerCount} of {report.peersCompared} similar districts, across {headline.programCount}{' '}
          {headline.programCount === 1 ? 'program' : 'programs'}, over the last five years.
        </p>
      </div>
    );
  }

  return (
    <h2 className="g-display max-w-[24ch] text-balance text-[clamp(1.75rem,4.5vw,2.75rem)] font-extrabold leading-[1.05]">
      {headline.count} {headline.count === 1 ? 'grant' : 'grants'} {name} can go after right now.
    </h2>
  );
}

function GrantRow({
  match,
  peersCompared,
  onFocusChange,
}: {
  match: GrantMatch;
  peersCompared: number;
  onFocusChange: (focused: boolean) => void;
}) {
  const status = grantStatusLabel(match);
  const award = grantAwardLabel(match);
  const body = (
    <>
      <span className="g-display block text-lg font-bold leading-snug">{match.title}</span>
      <span className="mt-1 block text-sm text-[var(--g-ink-soft)]">{grantReason(match, peersCompared)}</span>
      <span className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--g-slate)]">
        {status && (
          <span
            className={`rounded-sm px-1.5 py-0.5 font-semibold ${
              match.status === 'open' ? 'bg-[var(--g-go-wash)] text-[var(--g-go)]' : 'bg-[var(--g-accent-wash)] text-[var(--g-ink)]'
            }`}
          >
            {status}
          </span>
        )}
        {award && <span>{award}</span>}
        {match.agency && <span>{match.agency}</span>}
      </span>
    </>
  );

  return (
    <li
      className="border-b border-[var(--g-line)] transition-colors hover:bg-[var(--g-surface)] focus-within:bg-[var(--g-surface)]"
      onMouseEnter={() => onFocusChange(true)}
      onMouseLeave={() => onFocusChange(false)}
    >
      <div className="flex gap-4 py-4 pl-4 pr-3">
        <div className="min-w-0 flex-1" tabIndex={0} onFocus={() => onFocusChange(true)} onBlur={() => onFocusChange(false)}>
          {body}
        </div>
        {match.url && (
          <a
            href={match.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 h-fit shrink-0 text-[var(--g-slate)] hover:text-[var(--g-ink)]"
            aria-label={`Details for ${match.title} (opens in a new tab)`}
          >
            <ExternalLink className="size-4" />
          </a>
        )}
      </div>
    </li>
  );
}

const BONDS_FOCUS_ID = 'bonds';

/**
 * Capital bonds as a layer over the grants: what similar districts passed at the ballot box and
 * where this district stands. Pointing at it lights up the peers that passed bonds — the same
 * gesture as a grant row. Leads into the capital-planning pitch.
 */
function BondPanel({
  summary,
  source,
  onFocusChange,
}: {
  summary: BondSummary;
  source: string;
  onFocusChange: (focused: boolean) => void;
}) {
  return (
    <section
      tabIndex={0}
      aria-label="School bonds"
      onMouseEnter={() => onFocusChange(true)}
      onMouseLeave={() => onFocusChange(false)}
      onFocus={() => onFocusChange(true)}
      onBlur={() => onFocusChange(false)}
      className="mb-6 rounded-md bg-[var(--g-ink)] p-5 text-white"
    >
      <div className="flex items-start gap-3">
        <Landmark className="mt-1 size-5 shrink-0 text-[var(--g-accent)]" aria-hidden />
        <div className="space-y-2">
          <h3 className="g-display text-lg font-bold">School bonds</h3>
          {summary.peersLine && <p className="text-[15px] leading-snug">{summary.peersLine}</p>}
          <p className="text-[15px] leading-snug text-white/85">{summary.anchorLine}</p>
          <p className="text-sm text-white/70">Planning a bond? FMX helps districts build the capital plan that makes the case for it.</p>
          <p className="text-[11px] text-white/50">Passed new-money bond questions; refinancings excluded. Source: {source}.</p>
        </div>
      </div>
    </section>
  );
}

/**
 * The results: a headline built from the district's real funding position,
 * the peer map, and the grant list. Hovering or focusing a grant lights up the
 * peers that won it. Used by the live finder and the shared report.
 */
export function GrantReportView({ report }: { report: GrantReport }) {
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const bonds = useMemo(() => bondSummary(report), [report]);

  const highlighted = useMemo(() => {
    if (focusedId === BONDS_FOCUS_ID) return new Set(bonds?.passedPeerIds ?? []);
    const focused = report.matches.find((m) => m.id === focusedId);
    return new Set(focused?.peerDistrictIds ?? []);
  }, [focusedId, report.matches, bonds]);

  const hasMap = report.peers.length > 0;

  return (
    <div className="space-y-8">
      <Headline report={report} />

      <div className={hasMap ? 'grid gap-6 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]' : ''}>
        {hasMap && (
          <figure className="space-y-2">
            <div className="grants-map h-56 overflow-hidden rounded-md border border-[var(--g-line)] md:sticky md:top-4 md:h-[26rem]">
              <GrantPeerMap report={report} highlightedPeerIds={highlighted} />
            </div>
            <figcaption className="text-xs text-[var(--g-slate)]">
              {report.districtName} and the {report.peers.length} districts most like it by size, poverty, spending and locale.
              {' Point at or tap a grant to see who won it.'}
              {bonds?.passedPeerIds.length ? ' Point at School bonds to see who passed one.' : ''}
            </figcaption>
          </figure>
        )}

        <div>
          {bonds && report.bonds && (
            <BondPanel
              summary={bonds}
              source={report.bonds.source}
              onFocusChange={(focused) =>
                setFocusedId((cur) => (focused ? BONDS_FOCUS_ID : cur === BONDS_FOCUS_ID ? null : cur))
              }
            />
          )}
          <ul className="border-t border-[var(--g-line)]">
            {report.matches.map((m) => (
              <GrantRow
                key={m.id}
                match={m}
                peersCompared={report.peersCompared}
                onFocusChange={(focused) => setFocusedId((cur) => (focused ? m.id : cur === m.id ? null : cur))}
              />
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
